import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type PrizeDto = {
  PrizeId: number;
  code: string;
  name: string;
  type: string;
  valueJson: string;
};

type WheelSegment = {
  key: string;
  labelKey: string; // ✅ i18n key
};

type Props = {
  segments?: WheelSegment[];
  onSpin: () => Promise<PrizeDto>;
  mapPrizeToKey?: (p: PrizeDto) => string;
  onDone?: (p: PrizeDto) => void;
};

const DEFAULT_SEGMENTS: WheelSegment[] = [
  { key: "CP_PCT_10", labelKey: "voucherGame.wheel.segment.pct10" },
  { key: "CP_FIX_50K", labelKey: "voucherGame.wheel.segment.fix50k" },
  { key: "FS_30K", labelKey: "voucherGame.wheel.segment.freeship" },
  { key: "PT_100", labelKey: "voucherGame.wheel.segment.plus100" },
  { key: "OTHER", labelKey: "voucherGame.wheel.segment.lucky" },
  { key: "CP_PCT_10_2", labelKey: "voucherGame.wheel.segment.pct10" },
  { key: "CP_FIX_50K_2", labelKey: "voucherGame.wheel.segment.fix50k" },
  { key: "FS_30K_2", labelKey: "voucherGame.wheel.segment.freeship" },
  { key: "PT_100_2", labelKey: "voucherGame.wheel.segment.plus100" },
  { key: "CP_PCT_10_3", labelKey: "voucherGame.wheel.segment.pct10" },
  { key: "FS_30K_3", labelKey: "voucherGame.wheel.segment.freeship" },
  { key: "CP_FIX_50K_3", labelKey: "voucherGame.wheel.segment.fix50k" },
];

function defaultMapPrizeToKey(p: PrizeDto) {
  return p.code || "OTHER";
}

export default function WheelGame({
  segments = DEFAULT_SEGMENTS,
  onSpin,
  mapPrizeToKey = defaultMapPrizeToKey,
  onDone,
}: Props) {
  const { t } = useTranslation();

  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<PrizeDto | null>(null);

  const wheelRef = useRef<HTMLDivElement | null>(null);
  const lastRotationRef = useRef(0);

  const segmentAngle = useMemo(() => 360 / segments.length, [segments.length]);
  const skew = useMemo(() => 90 - segmentAngle, [segmentAngle]);

  const spinToIndex = (stopIndex: number, prize: PrizeDto) => {
    const el = wheelRef.current;
    if (!el) return;

    const extraTurns = 6;
    const target =
      extraTurns * 360 + (360 - stopIndex * segmentAngle - segmentAngle / 2);

    const nextRotation = lastRotationRef.current + target;
    lastRotationRef.current = nextRotation;

    el.style.transition = "transform 4.2s cubic-bezier(0.15, 0.85, 0.2, 1)";
    el.style.transform = `rotate(${nextRotation}deg)`;

    window.setTimeout(() => {
      setResult(prize);
      setSpinning(false);
      onDone?.(prize);
    }, 4300);
  };

  const handleSpin = async () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);

    try {
      const prize = await onSpin();
      const prizeKey = mapPrizeToKey(prize);

      let idx = segments.findIndex((s) => s.key === prizeKey);

      // fallback: nếu backend trả code lạ, thử match theo name (tối giản)
      if (idx === -1) {
        const name = (prize.name || "").toLowerCase();
        idx = segments.findIndex((s) =>
          t(s.labelKey).toLowerCase().includes(name)
        );
      }

      if (idx === -1) idx = Math.floor(Math.random() * segments.length);

      spinToIndex(idx, prize);
    } catch (e) {
      setSpinning(false);
      throw e;
    }
  };

  return (
    <div className="text-center">
      {/* title */}
      <div className="mb-4">
        <p className="text-[9px] uppercase tracking-[0.6em] text-black/35">
          {t("voucherGame.wheel.kicker")}
        </p>
        <h3 className="mt-3 text-[14px] uppercase tracking-[0.35em] text-black/80">
          {t("voucherGame.wheel.title")}
        </h3>
        <div className="mt-4 h-px w-10 bg-black/15 mx-auto" />
      </div>

      {/* pointer */}
      <div className="flex justify-center mb-2">
        <div
          className="
            w-0 h-0
            border-l-[10px] border-l-transparent
            border-r-[10px] border-r-transparent
            border-b-[16px] border-b-black/70
            drop-shadow-sm
          "
          aria-hidden="true"
        />
      </div>

      {/* wheel frame */}
      <div className="mx-auto w-[300px] h-[300px] sm:w-[330px] sm:h-[330px]">
        <div
          className="
            relative w-full h-full rounded-full
            border-[10px] border-black/70
            bg-white/60
            shadow-sm
            overflow-hidden
          "
        >
          {/* wheel itself */}
          <div
            ref={wheelRef}
            className="absolute inset-0 rounded-full"
            style={{ transform: "rotate(0deg)" }}
          >
            {segments.map((seg, i) => {
              const isEven = i % 2 === 0;

              return (
                <div
                  key={`${seg.key}-${i}`}
                  className="absolute w-1/2 h-1/2 top-1/2 left-1/2 border border-black/10"
                  style={{
                    transformOrigin: "0% 0%",
                    transform: `rotate(${i * segmentAngle}deg) skewY(${skew}deg)`,
                    background: isEven
                      ? "rgba(255,255,255,0.72)"
                      : "rgba(255,255,255,0.45)",
                  }}
                >
                  {/* label */}
                  <div
                    className="absolute left-3 top-3 w-[145px] text-left"
                    style={{
                      transformOrigin: "0% 0%",
                      transform: `skewY(-${skew}deg) rotate(${segmentAngle / 2}deg)`,
                    }}
                  >
                    <p className="text-[10px] sm:text-[11px] font-semibold text-black/70 uppercase tracking-[0.18em]">
                      {t(seg.labelKey)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* center cap */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-14 h-14 rounded-full border border-black/15 bg-white/70 backdrop-blur shadow-sm flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-black/40" />
            </div>
          </div>
        </div>
      </div>

      {/* action button */}
      <button
        type="button"
        onClick={handleSpin}
        disabled={spinning}
        aria-label={t("voucherGame.wheel.spinAria")}
        className={`
          mt-4 w-full max-w-[260px] h-10 rounded-full
          text-[9.5px] uppercase tracking-[0.55em]
          transition-all duration-300
          ${
            spinning
              ? "bg-black/20 text-black/40 cursor-not-allowed"
              : "bg-brand-brown text-brand-ivory hover:opacity-95"
          }
        `}
      >
        {spinning ? (
          <span className="inline-flex items-center gap-2">
            <span className="h-4 w-4 rounded-full border-2 border-brand-ivory/40 border-t-brand-ivory animate-spin" />
            {t("voucherGame.wheel.spinning")}
          </span>
        ) : (
          t("voucherGame.wheel.spin")
        )}
      </button>

      {/* result */}
      {result && (
        <div className="mt-5">
          <p className="text-[9px] uppercase tracking-[0.6em] text-black/35">
            {t("voucherGame.wheel.rewardKicker")}
          </p>

          <div className="mt-3 rounded-2xl border border-black/10 bg-white/60 px-4 py-4">
            <p className="text-[11px] text-black/60">
              {t("voucherGame.wheel.youWin")}
            </p>

            <p className="mt-1 text-[12px] text-black">
              <span className="font-medium">{result.name}</span>{" "}
              <span className="text-black/50">
                ({result.type}) —{" "}
                <span className="font-medium text-black/70">{result.code}</span>
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
